import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmPage from "./CrmPage";
import { crmFetch, STAGES, formatMoney } from "./crmApi";
import "./Crm.css";

const BAR_MAX_PX = 120;

const MONTH_SHORT = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("en-US", { month: "short" }) + (Number(m) === 1 ? ` '${y.slice(2)}` : "");
};

/**
 * Where the business actually comes from: conversion, monthly trend, which
 * sources pay off, and why deals are lost. Bars are plain divs — a chart
 * library for six bars would cost more than it's worth.
 */
function CrmReports() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await crmFetch("/api/crm/reports"));
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Every field is guarded: an unexpected response shape must fall through to
  // the empty state, not throw. (The desktop ui-e2e walks this page against a
  // mock API and caught exactly that.)
  const t = data?.totals;
  const monthly = data?.monthly || [];
  const sources = data?.sources || [];
  const lostReasons = data?.lostReasons || [];
  const topClients = data?.topClients || [];
  const maxMonthLeads = Math.max(1, ...monthly.map((m) => m.leads || 0));
  const funnelMax = Math.max(1, ...STAGES.map((s) => data?.funnel?.[s.key] || 0));

  return (
    <CrmPage
      title="📈 Reports"
      subtitle="How your enquiries turn into bookings"
      loading={loading}
      planBlocked={planBlocked}
    >
      {!t || !t.leads ? (
        <div className="crm-empty">
          <span className="crm-empty-icon">📈</span>
          Nothing to report yet.
          <div className="crm-empty-hint">
            Once you've logged a few leads, this page shows your conversion rate, best
            sources and why deals are lost.
          </div>
        </div>
      ) : (
        <>
          <div className="crm-stats-row">
            <div className="crm-stat-card" style={{ borderTopColor: "#10b981" }}>
              <div className="crm-stat-value">{t.conversionRate}%</div>
              <div className="crm-stat-label">🎯 Enquiry → booking</div>
              <div className="crm-stat-extra">{t.booked} of {t.leads} leads</div>
            </div>
            <div className="crm-stat-card" style={{ borderTopColor: "#059669" }}>
              <div className="crm-stat-value">{formatMoney(t.bookedValue) || "—"}</div>
              <div className="crm-stat-label">💰 Booked value</div>
              {formatMoney(t.avgBookedValue) && (
                <div className="crm-stat-extra">{formatMoney(t.avgBookedValue)} average</div>
              )}
            </div>
            <div className="crm-stat-card" style={{ borderTopColor: "#3b82f6" }}>
              <div className="crm-stat-value">{t.open}</div>
              <div className="crm-stat-label">🔄 Still open</div>
            </div>
            <div className="crm-stat-card" style={{ borderTopColor: "#8b5cf6" }}>
              <div className="crm-stat-value">
                {t.avgDaysToBook > 0 ? Math.round(t.avgDaysToBook) : "—"}
              </div>
              <div className="crm-stat-label">⏱ Days to book</div>
              <div className="crm-stat-extra">enquiry → confirmed, on average</div>
            </div>
          </div>

          <div className="crm-overview-grid">
            <div className="crm-card">
              <h3 className="crm-section-title">📊 Last 12 months</h3>
              {monthly.length === 0 ? (
                <div className="crm-empty" style={{ padding: "20px 8px" }}>No activity yet.</div>
              ) : (
                <div className="crm-bars">
                  {monthly.map((m) => {
                    // Pixel heights, not percentages: a percentage height inside
                    // a flex-sized parent collapses to a hairline in the browser.
                    const leadsPx = Math.max(3, Math.round((m.leads / maxMonthLeads) * BAR_MAX_PX));
                    const bookedPx = m.leads ? Math.round((m.booked / m.leads) * leadsPx) : 0;
                    return (
                      <div className="crm-bar-col" key={m.month} title={`${m.leads} leads · ${m.booked} booked`}>
                        <div className="crm-bar-stack">
                          <div className="crm-bar crm-bar--leads" style={{ height: `${leadsPx}px` }}>
                            <div className="crm-bar crm-bar--booked" style={{ height: `${bookedPx}px` }} />
                          </div>
                        </div>
                        <span className="crm-bar-label">{MONTH_SHORT(m.month)}</span>
                        <span className="crm-bar-value">{m.leads}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="crm-legend">
                <span><i className="crm-swatch crm-swatch--leads" /> Enquiries</span>
                <span><i className="crm-swatch crm-swatch--booked" /> Booked</span>
              </div>
            </div>

            <div className="crm-card">
              <h3 className="crm-section-title">🧭 Pipeline funnel</h3>
              {STAGES.map((s) => {
                const count = data.funnel?.[s.key] || 0;
                return (
                  <div key={s.key} className="crm-funnel-row" onClick={() => navigate(`/crm/leads?stage=${s.key}`)}>
                    <span className="crm-funnel-label">{s.icon} {s.label}</span>
                    <div className="crm-funnel-track">
                      <div
                        className="crm-funnel-fill"
                        style={{ width: `${(count / funnelMax) * 100}%`, background: s.color }}
                      />
                    </div>
                    <span className="crm-funnel-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="crm-overview-grid" style={{ marginTop: 18 }}>
            <div className="crm-card">
              <h3 className="crm-section-title">📣 Where leads come from</h3>
              {sources.length === 0 ? (
                <div className="crm-empty" style={{ padding: "20px 8px" }}>No sources recorded.</div>
              ) : (
                <div className="crm-table-wrap" style={{ boxShadow: "none" }}>
                  <table className="crm-table" style={{ minWidth: 0 }}>
                    <thead>
                      <tr><th>Source</th><th>Leads</th><th>Booked</th><th>Rate</th><th>Value</th></tr>
                    </thead>
                    <tbody>
                      {sources.map((s) => (
                        <tr key={s.source} style={{ cursor: "default" }}>
                          <td className="cell-main">{s.source}</td>
                          <td>{s.leads}</td>
                          <td>{s.booked}</td>
                          <td style={{ color: s.conversion >= 30 ? "#059669" : "#6b7280", fontWeight: 600 }}>
                            {s.conversion}%
                          </td>
                          <td>{formatMoney(s.value) || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="crm-card">
              <h3 className="crm-section-title">🚫 Why leads are lost</h3>
              {lostReasons.length === 0 ? (
                <div className="crm-empty" style={{ padding: "20px 8px" }}>
                  No lost leads recorded — good sign.
                </div>
              ) : (
                lostReasons.map((r) => (
                  <div key={r.reason} className="crm-list-row">
                    <div className="crm-row-main">
                      <div className="crm-row-title">{r.reason}</div>
                    </div>
                    <span className="crm-badge" style={{ background: "#fee2e2", color: "#b91c1c" }}>
                      {r.count}
                    </span>
                  </div>
                ))
              )}

              <h3 className="crm-section-title" style={{ marginTop: 20 }}>🏆 Best clients</h3>
              {topClients.length === 0 ? (
                <div className="crm-empty" style={{ padding: "20px 8px" }}>No bookings yet.</div>
              ) : (
                topClients.map((c) => (
                  <div key={c.name} className="crm-list-row">
                    <div className="crm-row-main">
                      <div className="crm-row-title">{c.name}</div>
                      <div className="crm-row-sub">{c.booked} booking{c.booked === 1 ? "" : "s"}</div>
                    </div>
                    <span className="crm-lead-amount">{formatMoney(c.value)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </CrmPage>
  );
}

export default CrmReports;
